'use client';

import classNames from 'classnames';
import { IFreeConsultationMailRequestBody } from '@sapientpro/sapientpro-data-models';
import { useEffect, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/navigation';
import ReCAPTCHA from 'react-google-recaptcha';
import styles from './Consultation.module.scss';
import { GAEmit, GAEvents } from '../../../gtagHelper';
import useDebouncedCallback from '../../../useDebounceCallback';
import { theme } from '../../../store/theme';
import client from '../../../apiClient';
import Img from '../../../components/Img';
import Button, { ButtonVariants } from '../../../components/Button';
import { CAPTCHA_SITE_KEY } from '../../../../constants';
import { buildCaptchaProtectedPayload } from '../../../utils/captcha';

enum SubmitStatus {
  ERROR = 'error',
  SUCCESS = 'success',
}

type Errors = {
  email?: string,
};

type SubscribeResponse = {
  message: string;
};

type ConsultationFormPayload = IFreeConsultationMailRequestBody & {
  captchaToken?: string;
};

export enum SubmitType {
  NEWSLETTER = 'newsletter',
}

export enum SubmitButtonView {
  inline = 'inline',
  full = 'full',
}

interface IConsultationFormProps {
  type?: SubmitType;
  className?: string;
  formId?: string;
  noRedirect?: boolean;
  submitButtonView?: SubmitButtonView;
  submitButtonText?: string;
  formClassNames?: {
    input?: string;
    button?: string;
    status?: string;
  }
}

const ConsultationForm = ({
  type,
  className,
  formId,
  noRedirect,
  submitButtonView = SubmitButtonView.inline,
  submitButtonText,
  formClassNames,
}: IConsultationFormProps) => {
  const router = useRouter();
  const appTheme = useAtomValue(theme);
  const [inputValue, setInputValue] = useState<string>('');
  const [isFormMutated, setIsFormMutated] = useState<boolean>(false);
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus | null>(null);
  const [customServerError, setCustomServerError] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const validateForm = () => {
    setErrors({});
    let currentErrors: Errors = {};
    if (inputValue !== '' && !/\S+@\S+\.\S+/.test(inputValue)) {
      currentErrors.email = 'Please enter a valid email';
    } else if (inputValue === '') {
      currentErrors.email = 'Please provide your email address';
    }
    setErrors(currentErrors);
    return Object.keys(currentErrors).length === 0;
  };

  const validateMutatedForm = useDebouncedCallback(() => {
    validateForm();
  }, 300);

  useEffect(() => {
    if (isFormMutated) {
      validateMutatedForm();
    }
  }, [inputValue, isFormMutated]);

  const submitForm = async (formData: ConsultationFormPayload) => {
    setIsLoading(true);
    setSubmitStatus(null);
    setCustomServerError(null);
    try {
      if (type === SubmitType.NEWSLETTER) {
        const subscribe = await client.getResponse<SubscribeResponse>(`news-subscription/subscribe?email=${formData.email}&us=false`);
        if (subscribe.status === 304) {
          setCustomServerError('You are already subscribed to our newsletter');
          setSubmitStatus(SubmitStatus.ERROR);
        } else if (subscribe.status === 429) {
          const { message } = subscribe.response;
          setCustomServerError(message);
          setSubmitStatus(SubmitStatus.ERROR);
        } else if (subscribe.status === 200) {
          setSubmitStatus(SubmitStatus.SUCCESS);
          !noRedirect && router.push('/subscribed');
          if (formRef.current) {
            formRef.current.reset();
          }

          setInputValue('');
        } else {
          setSubmitStatus(SubmitStatus.ERROR);
        }
      } else {
        const subscribe = await client.postResponse<SubscribeResponse>('contact-form-lead/consultation', {
          cache: 'no-cache',
          body: JSON.stringify(formData),
        });
        if (subscribe.status === 429) {
          const { message } = subscribe.response;
          setCustomServerError(message);
          setSubmitStatus(SubmitStatus.ERROR);
        } else if (subscribe.status === 200) {
          setSubmitStatus(SubmitStatus.SUCCESS);
          if (formRef.current) {
            formRef.current.reset();
          }

          setInputValue('');
        } else {
          setSubmitStatus(SubmitStatus.ERROR);
        }
      }
    } catch (e) {
      console.error('subscribe error', e);
      setSubmitStatus(SubmitStatus.ERROR);
    } finally {
      setIsLoading(false);
      recaptchaRef.current?.reset();
    }
  };

  const handleFormSubmit = (captchaToken: string | null) => {
    setIsFormMutated(false);
    const isFormValid = validateForm();
    GAEmit(GAEvents.consultationFormSubmission);
    if (!isFormValid) {
      recaptchaRef.current?.reset();
      return;
    }

    try {
      const formValues = { email: inputValue };
      const formData = type === SubmitType.NEWSLETTER
        ? formValues
        : buildCaptchaProtectedPayload(formValues, captchaToken);
      submitForm(formData);
    } catch (error) {
      setSubmitStatus(SubmitStatus.ERROR);
      setCustomServerError(error instanceof Error ? error.message : 'Captcha verification failed. Please try again.');
      recaptchaRef.current?.reset();
    }
  };

  const handleSubmit = () => {
    if (recaptchaRef.current) {
      recaptchaRef.current?.execute();
    }
  };

  const handleChange = (value: string) => {
    setSubmitStatus(null);
    setInputValue(value);
    setIsFormMutated(true);
  };

  const isEmailValid = !errors.email;
  const errorMessage = customServerError || errors?.email || 'Form submission error. Try again';
  const successMessage = 'The form has been successfully submitted';
  const submitMessage = submitStatus === SubmitStatus.SUCCESS ? successMessage : errorMessage;
  const showMessage = !!submitStatus || errors.email;
  const messageStyle = submitStatus === SubmitStatus.SUCCESS ? 'success' : 'error';
  const message = showMessage ? submitMessage : '';

  return (
    <form
      className={classNames(styles.form, className, {
        [styles.withFullButton]: submitButtonView === SubmitButtonView.full,
      })}
      id={formId}
      onSubmit={e => {
        e.preventDefault();
        handleSubmit();
      }}
      ref={formRef}
    >
      <div
        className={styles.inputWrap}
      >
        <input
          type='email'
          placeholder='Enter your email'
          className={classNames(styles.input, formClassNames?.input)}
          value={inputValue}
          onChange={e => handleChange(e.target.value)}
        />
        {submitButtonView === SubmitButtonView.inline && (
        <button
          type='submit'
          className={classNames(styles.submitButton, styles[submitButtonView])}
          aria-label='Get consultation'
          disabled={isLoading || !isEmailValid}
        >
          {isLoading ? (
            <img
              className={styles.spinner}
              src={`/media/spinner_${appTheme}.webp`}
              alt='spinner'
            />
          ) : (
            <svg>
              <use
                xlinkHref='/media/arrow-right-bold.svg#arrowRight'
                href='/media/arrow-right-bold.svg#arrowRight'
              />
            </svg>
          )}
        </button>
        )}
      </div>
      <div className={styles.line}>
        <Img
          src={`/media/consultation/consultationLine_${appTheme}.svg`}
          alt='consultation line'
        />
      </div>
      {(message) && (
        <p className={classNames(styles.status, styles[messageStyle], formClassNames?.status)}>
          {message}
        </p>
      )}

      {submitButtonView === SubmitButtonView.full && (
      <Button
        type='submit'
        className={classNames(styles.submitButton, styles[submitButtonView], formClassNames?.button)}
        variant={ButtonVariants.PRIMARY}
      >
        {submitButtonText || 'submit'}
      </Button>
      )}
      <ReCAPTCHA
        ref={recaptchaRef}
        size='invisible'
        sitekey={CAPTCHA_SITE_KEY || ''}
        onChange={handleFormSubmit}
      />
    </form>
  );
};

export default ConsultationForm;
